/**
 * Chantier : -
 * Regle metier : USR-R01..R04, PART-R06, INT-PL-12..18 (PRODUIT/domaine/15_autorisations.md, 17_documents_partenaires.md, 13_integrations.md)
 * Decision : D-INT-PL-17-URL-V1 (PLAN/decisions_developpement.md)
 * Hooks React Query du module etablissement : CRUD, onboarding (les lignes de tarif
 * de vente sont generees en BDD par trigger, TARIF-R02b), liaison Pennylane variante A (recherche SIRET/ID PL,
 * link/unlink avec discriminants kind, comptage objets PL actifs pour conditionner
 * la modifiabilite du PL_ID).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchEtablissements,
  fetchEtablissement,
  fetchSermeEtablissement,
  updateEtablissement,
  onboardEtablissement,
  searchPennylaneCustomers,
  type PennylaneSearchMode,
  linkPennylaneCustomer,
  unlinkPennylaneCustomer,
  countActivePlObjects,
  type Etablissement,
  type UpdateEtablissementInput,
  type OnboardInput,
} from '@/services/etablissements';

// Filtres hors du hook : une reference stable, sinon `select` recalcule une nouvelle liste a chaque rendu.
const selectClients = (liste: Etablissement[]) => liste.filter((e) => !e.isSerme);
const selectClientsActifs = (liste: Etablissement[]) => liste.filter((e) => !e.isSerme && e.isActive);

// Clients de SERME : jamais l'etablissement SERME (TARIF-R02). `actifs` : seulement ceux proposables dans un
// formulaire, un client desactive est invisible des listes de choix (USR-R04). La gestion (/etablissements,
// prix) garde les desactives.
export function useEtablissementsClients({ actifs }: { actifs: boolean }) {
  return useQuery({
    queryKey: ['etablissements'],
    queryFn: fetchEtablissements,
    select: actifs ? selectClientsActifs : selectClients,
  });
}

export function useEtablissement(id: string | undefined) {
  return useQuery({
    queryKey: ['etablissements', id],
    queryFn: () => fetchEtablissement(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// PART-R06 : singleton SERME. Une seule ligne (etablissements.is_serme = true), garantie
// par index unique partiel BDD. Cache long car ces donnees varient peu (edition manuelle).
export function useSermeEtablissement() {
  return useQuery({
    queryKey: ['etablissements', 'serme'],
    queryFn: fetchSermeEtablissement,
    staleTime: 5 * 60 * 1000,
  });
}

export function useOnboardEtablissement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: OnboardInput) => onboardEtablissement(input),
    // Promesse renvoyee : mutateAsync attend les listes rechargees, la page ouvre ensuite la fiche
    // du nouvel etablissement sans afficher l'ancienne liste (audit /etablissements E2, E6).
    onSuccess: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: ['etablissements'] }),
      queryClient.invalidateQueries({ queryKey: ['users'] }),
      // Les lignes de tarif de vente du nouveau client sont generees en BDD dans le meme
      // INSERT (trigger, TARIF-R02b / D-TARIF-GEN-BDD) : on invalide le cache de la grille.
      queryClient.invalidateQueries({ queryKey: ['tarifs'] }),
    ]),
  });
}

export function useUpdateEtablissement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateEtablissementInput }) =>
      updateEtablissement(id, input),
    // Promesse renvoyee : la mutation reste en cours jusqu'a la liste rechargee, un second ajout ou
    // retrait de partenaire ne part pas d'une liste perimee (audit /etablissements E4). Le prefixe
    // couvre aussi ['etablissements', id].
    // NB : la (re)activation d'un etablissement ne passe PAS par ce hook (USR-R04 : operation
    // OPS manuelle en SQL, pas d'UI V1). Les lignes de tarif de vente manquantes sont generees
    // en BDD a la reactivation (trigger sur etablissements.is_active, TARIF-R02b).
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['etablissements'] }),
  });
}

// =============================================================================
// Liaison Pennylane (DV-2026-0061 LOT-03 ; INT-PL-12 a INT-PL-18)
// =============================================================================

// INT-PL-13 variante A : recherche deterministe par SIRET ou ID PL natif (declenche on demand).
export function useSearchPennylaneCustomers(query: string, mode: PennylaneSearchMode, enabled: boolean) {
  return useQuery({
    queryKey: ['pennylane-customers', 'search', mode, query],
    queryFn: () => searchPennylaneCustomers(query, mode),
    enabled: enabled && query.trim().length > 0,
    staleTime: 30 * 1000,
    // L'EF rejoue deja en interne ; la modale affiche l'erreur elle-meme.
    retry: false,
    meta: { toastErreur: false },
  });
}

// INT-PL-13/15 : lier un customer PL existant. Retour discrimine pour permettre au front
// de distinguer customer_already_used / already_linked / linked / error.
export function useLinkPennylaneCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ etablissementId, plCustomerId }: { etablissementId: string; plCustomerId: string }) =>
      linkPennylaneCustomer(etablissementId, plCustomerId),
    onSuccess: (result) => {
      if (result.kind === 'linked') {
        queryClient.invalidateQueries({ queryKey: ['etablissements'] });
        queryClient.invalidateQueries({ queryKey: ['pennylane-customers', 'search'] });
      }
    },
  });
}

// INT-PL-16 : delie le customer PL d'un etablissement.
export function useUnlinkPennylaneCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (etablissementId: string) => unlinkPennylaneCustomer(etablissementId),
    onSuccess: (_data, etablissementId) => {
      // Le prefixe ['etablissements'] couvre aussi la fiche ['etablissements', id].
      queryClient.invalidateQueries({ queryKey: ['etablissements'] });
      queryClient.invalidateQueries({ queryKey: ['count-active-pl-objects', etablissementId] });
    },
  });
}

// INT-PL-16 : compte des objets PL actifs pour conditionner la modifiabilite du PL_ID.
// Inutile de declencher la query tant que l'etablissement n'a pas de pennylane_customer_id :
// pas de garde de modifiabilite a calculer.
export function useCountActivePlObjects(
  etablissementId: string | null | undefined,
  pennylaneCustomerId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['count-active-pl-objects', etablissementId],
    queryFn: () => countActivePlObjects(etablissementId!),
    enabled: !!etablissementId && !!pennylaneCustomerId,
    staleTime: 60 * 1000,
  });
}
